import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useGoalsStore } from './store';
import { GoalModal } from './goal-modal';
import { GoalCard } from './goal-card';
import { Checkbox } from '@components/checkbox';
import { Select } from '@components/select';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import {
    CommunityDailyGoalRequestV3Dto,
    CommunityGoalAdminUpdateRequestDto,
    GoalDto,
    RecurrenceType
} from '@api/FitnessActivityClient';
import {
    describeGoal,
    goalActivityScope,
    goalTitle,
    isGoalEnded,
    recurrenceLabels
} from '@core/goals/goal-summary';
import { useParams } from 'react-router';

type VisibilityFilter = 'ALL' | 'VISIBLE' | 'HIDDEN';

const recurrenceFilterOptions = [
    { label: 'All', value: 'ALL' as RecurrenceType | 'ALL' },
    ...Object.values(RecurrenceType).map((recurrence) => ({
        label: recurrenceLabels[recurrence],
        value: recurrence as RecurrenceType | 'ALL'
    }))
];

const visibilityFilterOptions: { label: string; value: VisibilityFilter }[] = [
    { label: 'All', value: 'ALL' },
    { label: 'Visible', value: 'VISIBLE' },
    { label: 'Hidden', value: 'HIDDEN' }
];

const matchesSearch = (goal: GoalDto, search: string): boolean => {
    const haystack = [
        goal.name,
        describeGoal(goal.rule, goal.recurrence),
        goalActivityScope(goal.rule),
        goal.key
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    return haystack.includes(search.trim().toLowerCase());
};

export const CommunityGoalsSection: React.FC = () => {
    const { communityId } = useParams();
    const { goals, handleCreateGoal, handleUpdateGoal, handleDeleteGoal } =
        useGoalsStore(parseInt(communityId));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [goalToEdit, setGoalToEdit] = useState<GoalDto | undefined>(
        undefined
    );
    const [goalToDelete, setGoalToDelete] = useState<GoalDto | undefined>(
        undefined
    );
    const [search, setSearch] = useState('');
    const [recurrenceFilter, setRecurrenceFilter] = useState<
        RecurrenceType | 'ALL'
    >('ALL');
    const [visibilityFilter, setVisibilityFilter] =
        useState<VisibilityFilter>('ALL');
    const [showEnded, setShowEnded] = useState(false);

    useEffect(() => {
        goals.request();
    }, [goals]);

    const allGoals: GoalDto[] = goals.result || [];
    const endedCount = useMemo(
        () => allGoals.filter((goal) => isGoalEnded(goal)).length,
        [allGoals]
    );

    const matchingGoals = useMemo(
        () =>
            allGoals.filter((goal) => {
                if (!showEnded && isGoalEnded(goal)) {
                    return false;
                }
                if (search && !matchesSearch(goal, search)) {
                    return false;
                }
                if (
                    recurrenceFilter !== 'ALL' &&
                    goal.recurrence !== recurrenceFilter
                ) {
                    return false;
                }
                if (visibilityFilter === 'VISIBLE' && !goal.visibleInApp) {
                    return false;
                }
                if (visibilityFilter === 'HIDDEN' && goal.visibleInApp) {
                    return false;
                }
                return true;
            }),
        [allGoals, search, recurrenceFilter, visibilityFilter, showEnded]
    );

    const handleOpenModal = () => {
        setGoalToEdit(undefined);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (goal: GoalDto) => {
        setGoalToEdit(goal);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setGoalToEdit(undefined);
    };

    const handleCreate = (
        goalData: CommunityDailyGoalRequestV3Dto,
        assignAllUsers: boolean
    ) => handleCreateGoal(goalData, assignAllUsers);

    const handleUpdate = (
        key: string,
        goalData: CommunityGoalAdminUpdateRequestDto
    ) => handleUpdateGoal(key, goalData);

    const handleConfirmDelete = async () => {
        if (goalToDelete) {
            await handleDeleteGoal(goalToDelete.key);
        }
        setGoalToDelete(undefined);
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Community Goals</h3>
                <Button onClick={handleOpenModal}>
                    <Plus />
                    Add Goal
                </Button>
            </div>
            {goals.match({
                default: () => <div></div>,
                loading: () => (
                    <div className="mt-1 flex h-32 items-center justify-center">
                        <Spinner />
                    </div>
                ),
                error: () => (
                    <Alert variant="destructive">
                        <AlertDescription>
                            Error loading goals: {goals.error!.message}
                        </AlertDescription>
                    </Alert>
                ),
                fetched: () => (
                    <div>
                        <div className="mb-4 flex flex-wrap items-end gap-3">
                            <div className="relative min-w-[220px]">
                                <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    className="pl-9"
                                    placeholder="Search goals"
                                    value={search}
                                    onChange={(event) =>
                                        setSearch(event.target.value)
                                    }
                                />
                            </div>
                            <div className="min-w-[150px]">
                                <Select
                                    label="Recurrence"
                                    value={recurrenceFilter}
                                    options={recurrenceFilterOptions}
                                    onChange={(value: RecurrenceType | 'ALL') =>
                                        setRecurrenceFilter(value)
                                    }
                                />
                            </div>
                            <div className="min-w-[150px]">
                                <Select
                                    label="In mobile app"
                                    value={visibilityFilter}
                                    options={visibilityFilterOptions}
                                    onChange={(value: VisibilityFilter) =>
                                        setVisibilityFilter(value)
                                    }
                                />
                            </div>
                            {endedCount > 0 && (
                                <div className="pb-2">
                                    <Checkbox
                                        center
                                        checked={showEnded}
                                        onChange={(_, checked) =>
                                            setShowEnded(checked)
                                        }
                                    >
                                        Show ended ({endedCount})
                                    </Checkbox>
                                </div>
                            )}
                            <p className="pb-2 text-sm text-muted-foreground">
                                {matchingGoals.length === allGoals.length
                                    ? `${allGoals.length} goals`
                                    : `${matchingGoals.length} of ${allGoals.length} goals`}
                            </p>
                        </div>

                        {matchingGoals.length === 0 ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">
                                No goals match these filters.
                            </p>
                        ) : (
                            <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
                                {matchingGoals.map((goal) => (
                                    <GoalCard
                                        key={goal.id}
                                        goal={goal}
                                        onEdit={handleOpenEditModal}
                                        onDelete={setGoalToDelete}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ),
                empty: () => (
                    <div>
                        <h4 className="text-lg font-semibold">No goals yet</h4>
                        <p className="text-sm text-muted-foreground">
                            Create your first community goal to get started.
                        </p>
                    </div>
                )
            })}
            <GoalModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
                communityId={parseInt(communityId)}
                goal={goalToEdit}
            />
            <Dialog
                open={!!goalToDelete}
                onOpenChange={(next) => !next && setGoalToDelete(undefined)}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete goal</DialogTitle>
                        <DialogDescription>
                            {goalToDelete &&
                                `Delete "${goalTitle(goalToDelete)}"? Members stop being assigned to it and it disappears from the app. Progress and rewards already earned are kept.`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={() => setGoalToDelete(undefined)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
